import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import DynamicCard from '../components/dynamic/DynamicCard';
import DynamicTag from '../components/dynamic/DynamicTag';
import DynamicButton from '../components/dynamic/DynamicButton';
import { resourcesAPI } from '../services/api';
import { Resource } from '../types';
import { 
  FaBook, 
  FaVideo, 
  FaToolbox, 
  FaFileAlt, 
  FaGraduationCap,
  FaExternalLinkAlt,
  FaStar
} from 'react-icons/fa';

const typeIcons: { [key: string]: any } = {
  tool: FaToolbox,
  article: FaFileAlt,
  course: FaGraduationCap,
  book: FaBook,
  video: FaVideo,
  other: FaFileAlt,
};

const Resources: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    const loadResources = async () => {
      try {
        const response = await resourcesAPI.getAll();
        if (response && response.data) {
          setResources(Array.isArray(response.data) ? response.data : []);
        } else {
          setResources([]);
        }
      } catch (error: any) {
        console.error('Error loading resources:', error);
        setResources([]);
      } finally {
        setLoading(false);
      }
    };
    loadResources();
  }, []);

  const categories = resources.length > 0
    ? ['all', ...Array.from(new Set(resources.map(r => r.category)))]
    : ['all'];
  const types = resources.length > 0
    ? ['all', ...Array.from(new Set(resources.map(r => r.type)))]
    : ['all'];

  const filteredResources = resources.filter(r => {
    const categoryMatch = selectedCategory === 'all' || r.category === selectedCategory;
    const typeMatch = selectedType === 'all' || r.type === selectedType;
    return categoryMatch && typeMatch;
  });

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading resources...</div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <DynamicSection
        title="Resources & Tools"
        subtitle="Curated list of helpful resources, tools, and learning materials"
      >
        <div className="mb-8 space-y-4">
          <div className="flex flex-wrap gap-2 justify-center">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 self-center">Category:</span>
            {categories.map((category) => (
              <DynamicButton
                key={category}
                variant={selectedCategory === category ? 'primary' : 'outline'}
                onClick={() => setSelectedCategory(category)}
                className="capitalize"
              >
                {category}
              </DynamicButton>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 self-center">Type:</span>
            {types.map((type) => (
              <DynamicButton
                key={type}
                variant={selectedType === type ? 'primary' : 'outline'}
                onClick={() => setSelectedType(type)}
                className="capitalize"
              >
                {type}
              </DynamicButton>
            ))}
          </div>
        </div>

        {filteredResources.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No resources found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => {
            const IconComponent = typeIcons[resource.type] || FaFileAlt;
            return (
              <DynamicCard key={resource.id} className="p-6 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <IconComponent className="text-primary-600 dark:text-primary-400" />
                    <span className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      {resource.type}
                    </span>
                  </div>
                  {resource.featured && (
                    <FaStar className="text-yellow-400" />
                  )}
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {resource.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4 flex-grow text-sm">
                  {resource.description}
                </p>
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {resource.tags.slice(0, 3).map((tag) => (
                      <DynamicTag key={tag} variant="secondary" size="sm">
                        {tag}
                      </DynamicTag>
                    ))}
                  </div>
                </div>
                <DynamicButton
                  variant="outline"
                  onClick={() => window.open(resource.url, '_blank')}
                  className="w-full"
                >
                  Visit Resource
                  <FaExternalLinkAlt className="ml-2 inline" />
                </DynamicButton>
              </DynamicCard>
            );
            })}
          </div>
        )}
      </DynamicSection>
    </div>
  );
};

export default Resources;
